import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Skeleton } from '@mui/material';
import { Remove, CheckCircle, HighlightOff, Help } from '@mui/icons-material';
import { useScanCodeCountQuery, useVerifyCodeQuery } from '../features/api/rest/apiLotsSlice';
import LotInformation from './LotInformation';

function CodeInformation({ ean, code }) {
  const { t } = useTranslation();

  const { isFetching: isScanFetching, isLoading: isScanLoading } = useScanCodeCountQuery({ ean, code });
  const { data: lot = {}, isFetching, isLoading, isError, refetch } = useVerifyCodeQuery({ ean, code }, { refetchOnMountOrArgChange: true, refetchOnReconnect: true, refetchOnFocus: true });

  useEffect(() => {
    if (!isScanFetching && !isScanLoading) {
      refetch();
    }
  }, [isScanFetching, isScanLoading, refetch]);

  if (isFetching || isLoading) return <Skeleton animation="wave" />;

  if (isError || Object.keys(lot).length === 0)
    return (
      <ListItem sx={{ p: 0 }} divider>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'white', color: 'black', width: 24, height: 24 }}>{<Remove />}</Avatar>
        </ListItemAvatar>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'white', color: 'orange' }}>{<HighlightOff data-testid="highlight-off" />}</Avatar>
        </ListItemAvatar>
        <ListItemText primaryTypographyProps={{ display: 'inline' }} primary={t('products.lots.invalidated')} />
      </ListItem>
    );

  return (
    <>
      <ListItem sx={{ p: 0 }} divider>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'white', color: 'black', width: 24, height: 24 }}>{<Remove />}</Avatar>
        </ListItemAvatar>
        <ListItemAvatar>
          {lot.suspect ? (
            <Avatar sx={{ bgcolor: 'white', color: 'red' }}>
              <Help data-testid="help" />
            </Avatar>
          ) : (
            <Avatar sx={{ bgcolor: 'white', color: 'green' }}>
              <CheckCircle data-testid="check-circle" />
            </Avatar>
          )}
        </ListItemAvatar>
        <ListItemText primaryTypographyProps={{ display: 'inline' }} secondaryTypographyProps={{ display: 'inline', ml: 2 }} primary={lot.suspect ? t('products.lots.suspect') : t('products.lots.validated')} />
      </ListItem>
      <List component="div" disablePadding sx={{ ml: 3 }}>
        <LotInformation ean={ean} authority={lot.lot_tree.authority} code={lot.lot_tree.code} isRoot={false} />
      </List>
    </>
  );
}

CodeInformation.propTypes = {
  ean: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
};

export default CodeInformation;
